import { apiFetch } from "./utils";
import { API_ENDPOINTS } from "../config/api";
import {
  EventLocation,
  Tournament,
  TournamentEvent,
  ApiTournamentEvent,
  TournamentParticipant,
  CompetitionStage,
  CompetitionStageWritePayload,
  CompetitionGroup,
  Match,
  GroupStanding,
  ID,
} from "../types";

/** TOURNAMENT */
// Ottieni un torneo
export const getTournament = async (tournamentId: number): Promise<Tournament> => {
  return apiFetch(`${API_ENDPOINTS.TOURNAMENT(tournamentId)}`);
};
// Ottieni una lista di tornei
// seasonId: ID della stagione (opzionale)
// Se non specificato, restituisce tutti i tornei
export interface apiGetListTournaments {
  seasonId?: number; // ID della stagione (opzionale)
}
export const getListTournaments = async (options: apiGetListTournaments): Promise<Tournament[]> => {
  const params = new URLSearchParams();
  if (options.seasonId) params.append('season_id', options.seasonId.toString());
  return apiFetch(`${API_ENDPOINTS.TOURNAMENTS}?${params.toString()}`);
};

// Crea un torneo
export const createTournament = async (data: Partial<Tournament>) => {
  return apiFetch(API_ENDPOINTS.TOURNAMENTS, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
};
// Aggiorna un torneo
export const updateTournament = async (id: number, data: Partial<Tournament>) => {
  return apiFetch(API_ENDPOINTS.TOURNAMENT(id), {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
};
// DELETE torneo
export const deleteTournament = async (id: number): Promise<void> => {
  return apiFetch(API_ENDPOINTS.TOURNAMENT(id), { 
    method: "DELETE", 
    headers: { "Content-Type": "application/json" },
   });
};


/** TOURNAMENTS EVENT (contiene results eventualmente)*/
export const getTournamentEvent = async (id: number): Promise<TournamentEvent> => {
  return apiFetch(API_ENDPOINTS.TOURNAMENT_EVENT(id));
};
// Ottieni i risultati di un evento di un torneo in una stagione specifica
export interface apiGetTournamentEvents {
  tournamentId?: number; // ID del torneo (opzionale)
  seasonId?: number; // ID della stagione (opzionale)
}
export const getListTournamentEvents = async (options: apiGetTournamentEvents = {}): Promise<TournamentEvent[]> => {
  // Costruisci i parametri della query in base agli ID forniti
  const params = new URLSearchParams();
  if (options.tournamentId) params.append('tournament_id', options.tournamentId.toString());
  if (options.seasonId) params.append('season_id', options.seasonId.toString());
  return apiFetch(
        `${API_ENDPOINTS.TOURNAMENT_EVENTS}?${params.toString()}`
    );
};
// CREATE evento (con results opzionali)
export const createTournamentEvent = async (data: Partial<ApiTournamentEvent>): Promise<TournamentEvent> => {
  return apiFetch(API_ENDPOINTS.TOURNAMENT_EVENTS, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
};

// UPDATE evento (sovrascrive results[])
export const updateTournamentEvent = async (
  id: number,
  data: Partial<ApiTournamentEvent>
): Promise<TournamentEvent> => {
  return apiFetch(API_ENDPOINTS.TOURNAMENT_EVENT(id), {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
};

export const generateTournamentEventResults = async (
  id: number
): Promise<TournamentEvent> => {
  return apiFetch(API_ENDPOINTS.TOURNAMENT_EVENT_GENERATE_RESULTS(id), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
  });
};

// DELETE evento
export const deleteTournamentEvent = async (id: number): Promise<void> => {
  return apiFetch(API_ENDPOINTS.TOURNAMENT_EVENT(id), { 
    headers: { "Content-Type": "application/json" },
    method: "DELETE",
  });
};

/** EVENT LOCATIONS */

// Ottieni un torneo
export const getEventLocation = async (id: number): Promise<EventLocation> => {
  return apiFetch(`${API_ENDPOINTS.EVENT_LOCATION(id)}`);
};
export const getListEventLocations = async (): Promise<EventLocation[]> => {
  const params = new URLSearchParams();
  return apiFetch(`${API_ENDPOINTS.EVENT_LOCATIONS}?${params.toString()}`);
};

// Crea un torneo
export const createEventLocation = async (data: Partial<EventLocation>) => {
  return apiFetch(API_ENDPOINTS.EVENT_LOCATIONS, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
};
// Aggiorna un torneo
export const updateEventLocation = async (id: number, data: Partial<EventLocation>) => {
  return apiFetch(API_ENDPOINTS.EVENT_LOCATION(id), {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
};
// DELETE torneo
export const deleteEventLocation = async (id: number): Promise<void> => {
  return apiFetch(API_ENDPOINTS.EVENT_LOCATION(id), { 
    method: "DELETE", 
    headers: { "Content-Type": "application/json" },
   });
};

/** COMPETITION PARTICIPANTS */
export const getCompetitionParticipant = async (id: number): Promise<TournamentParticipant> => {
  return apiFetch(API_ENDPOINTS.TOURNAMENT_PARTICIPANT(id));
};

export const getListCompetitionParticipants = async (eventId?: ID): Promise<TournamentParticipant[]> => {
  const params = new URLSearchParams();
  if (eventId) params.append("event_id", eventId.toString());
  return apiFetch(`${API_ENDPOINTS.TOURNAMENT_PARTICIPANTS}?${params.toString()}`);
};

export const createCompetitionParticipant = async (
  data: Partial<TournamentParticipant>
): Promise<TournamentParticipant> => {
  return apiFetch(API_ENDPOINTS.TOURNAMENT_PARTICIPANTS, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
};

export const updateCompetitionParticipant = async (
  id: number,
  data: Partial<TournamentParticipant>
): Promise<TournamentParticipant> => {
  return apiFetch(API_ENDPOINTS.TOURNAMENT_PARTICIPANT(id), {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
};

export const deleteCompetitionParticipant = async (id: number): Promise<void> => {
  return apiFetch(API_ENDPOINTS.TOURNAMENT_PARTICIPANT(id), {
    method: "DELETE",
    headers: { "Content-Type": "application/json" },
  });
};

export interface BulkCompetitionParticipantsPayload {
  event: ID;
  participants: Array<Partial<TournamentParticipant> & { id?: ID }>;
  deleted_ids?: ID[];
}
export interface BulkCompetitionParticipantsResponse {
  detail: string;
  participants: TournamentParticipant[];
  created: TournamentParticipant[];
  updated: TournamentParticipant[];
  deleted_ids: ID[];
}
export const bulkUpsertCompetitionParticipants = async (
  data: BulkCompetitionParticipantsPayload
): Promise<BulkCompetitionParticipantsResponse> => {
  return apiFetch(API_ENDPOINTS.TOURNAMENT_PARTICIPANTS_BULK, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
};

/** COMPETITION STAGES */
export const getListCompetitionStages = async (eventId?: ID): Promise<CompetitionStage[]> => {
  const params = new URLSearchParams();
  if (eventId) params.append("event_id", eventId.toString());
  return apiFetch(`${API_ENDPOINTS.COMPETITION_STAGES}?${params.toString()}`);
};

export const createCompetitionStage = async (
  data: CompetitionStageWritePayload
): Promise<CompetitionStage> => {
  return apiFetch(API_ENDPOINTS.COMPETITION_STAGES, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
};

export const updateCompetitionStage = async (
  id: ID,
  data: CompetitionStageWritePayload
): Promise<CompetitionStage> => {
  return apiFetch(API_ENDPOINTS.COMPETITION_STAGE(Number(id)), {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
};

export const deleteCompetitionStage = async (id: ID): Promise<void> => {
  return apiFetch(API_ENDPOINTS.COMPETITION_STAGE(Number(id)), {
    method: "DELETE",
    headers: { "Content-Type": "application/json" },
  });
};

export const generateCompetitionStage = async (
  stageId: ID,
  participantIds?: ID[],
  matchDurationMinutes?: number
): Promise<{
  detail: string;
  participants_count: number;
  groups_created: number;
  matches_created: number;
  stage: CompetitionStage;
}> => {
  const payload = {
    ...(participantIds ? { participant_ids: participantIds } : {}),
    ...(matchDurationMinutes ? { match_duration_minutes: matchDurationMinutes } : {}),
  };
  const body = Object.keys(payload).length ? JSON.stringify(payload) : undefined;
  return apiFetch(API_ENDPOINTS.COMPETITION_STAGE_GENERATE(Number(stageId)), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    ...(body ? { body } : {}),
  });
};

export const generateKnockoutCompetitionStage = async (
  stageId: ID
): Promise<{
  detail: string;
  participants_count: number;
  matches_created: number;
  stage: CompetitionStage;
}> => {
  return apiFetch(API_ENDPOINTS.COMPETITION_STAGE_GENERATE_KNOCKOUT(Number(stageId)), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
  });
};

/** COMPETITION GROUPS */
export interface ApiListCompetitionGroupsOptions {
  eventId?: ID;
  stageId?: ID;
}
export const getListCompetitionGroups = async (
  options: ApiListCompetitionGroupsOptions = {}
): Promise<CompetitionGroup[]> => {
  const params = new URLSearchParams();
  if (options.eventId) params.append("event_id", options.eventId.toString());
  if (options.stageId) params.append("stage_id", options.stageId.toString());
  return apiFetch(`${API_ENDPOINTS.COMPETITION_GROUPS}?${params.toString()}`);
};

export const updateCompetitionGroupParticipants = async (
  groupId: ID,
  participantIds: ID[]
): Promise<{
  detail: string;
  participants_count: number;
  matches_created: number;
  available_participants: TournamentParticipant[];
  group: CompetitionGroup;
}> => {
  return apiFetch(API_ENDPOINTS.COMPETITION_GROUP_SET_PARTICIPANTS(Number(groupId)), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ participant_ids: participantIds }),
  });
};

/** MATCHES */
export interface ApiListCompetitionMatchesOptions {
  eventId?: ID;
  stageId?: ID;
  groupId?: ID;
  status?: string;
}
export const getListCompetitionMatches = async (
  options: ApiListCompetitionMatchesOptions = {}
): Promise<Match[]> => {
  const params = new URLSearchParams();
  if (options.eventId) params.append("event_id", options.eventId.toString());
  if (options.stageId) params.append("stage_id", options.stageId.toString());
  if (options.groupId) params.append("group_id", options.groupId.toString());
  if (options.status) params.append("status", options.status);
  return apiFetch(`${API_ENDPOINTS.COMPETITION_MATCHES}?${params.toString()}`);
};

export interface ExportCompetitionMatchesPdfOptions {
  seasonId: ID;
  startDate: string;
  endDate: string;
  userId?: ID | null;
  userLabel?: string;
}
export const exportCompetitionMatchesPdf = async (
  options: ExportCompetitionMatchesPdfOptions
): Promise<Blob> => {
  const params = new URLSearchParams({
    season_id: options.seasonId.toString(),
    start_date: options.startDate,
    end_date: options.endDate,
    tz: Intl.DateTimeFormat().resolvedOptions().timeZone,
  });
  if (options.userId) params.append("user_id", options.userId.toString());
  if (options.userLabel) params.append("user_label", options.userLabel);

  const response = await fetch(`${API_ENDPOINTS.COMPETITION_MATCHES_EXPORT_PDF}?${params.toString()}`);
  if (!response.ok) {
    throw new Error("Errore durante l'esportazione PDF.");
  }
  return response.blob();
};

export const exportCompetitionMatchesExcel = async (
  options: ExportCompetitionMatchesPdfOptions
): Promise<Blob> => {
  const params = new URLSearchParams({
    season_id: options.seasonId.toString(),
    start_date: options.startDate,
    end_date: options.endDate,
    tz: Intl.DateTimeFormat().resolvedOptions().timeZone,
  });
  if (options.userId) params.append("user_id", options.userId.toString());
  if (options.userLabel) params.append("user_label", options.userLabel);

  const response = await fetch(`${API_ENDPOINTS.COMPETITION_MATCHES_EXPORT_EXCEL}?${params.toString()}`);
  if (!response.ok) {
    throw new Error("Errore durante l'esportazione Excel.");
  }
  return response.blob();
};

export const exportTournamentEventMatchesPdf = async (eventId: ID): Promise<Blob> => {
  const params = new URLSearchParams({
    event_id: eventId.toString(),
    tz: Intl.DateTimeFormat().resolvedOptions().timeZone,
  });
  const response = await fetch(`${API_ENDPOINTS.COMPETITION_EVENT_MATCHES_EXPORT_PDF}?${params.toString()}`);
  if (!response.ok) {
    throw new Error("Errore durante l'esportazione PDF dell'evento.");
  }
  return response.blob();
};

export const exportTournamentEventMatchesExcel = async (eventId: ID): Promise<Blob> => {
  const params = new URLSearchParams({
    event_id: eventId.toString(),
    tz: Intl.DateTimeFormat().resolvedOptions().timeZone,
  });
  const response = await fetch(`${API_ENDPOINTS.COMPETITION_EVENT_MATCHES_EXPORT_EXCEL}?${params.toString()}`);
  if (!response.ok) {
    throw new Error("Errore durante l'esportazione Excel dell'evento.");
  }
  return response.blob();
};

export const createCompetitionMatch = async (data: Partial<Match>): Promise<Match> => {
  return apiFetch(API_ENDPOINTS.COMPETITION_MATCHES, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
};

export const updateCompetitionMatch = async (
  id: number,
  data: Partial<Match>
): Promise<Match> => {
  return apiFetch(API_ENDPOINTS.COMPETITION_MATCH(id), {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
};

export const deleteCompetitionMatch = async (id: number): Promise<void> => {
  return apiFetch(API_ENDPOINTS.COMPETITION_MATCH(id), {
    method: "DELETE",
  });
};

export const setCompetitionMatchResult = async (
  id: number,
  score1: number,
  score2: number,
  scheduled_at?: string | null
): Promise<Match> => {
  return apiFetch(API_ENDPOINTS.COMPETITION_MATCH_SET_RESULT(id), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ score1, score2, ...(scheduled_at !== undefined ? { scheduled_at } : {}) }),
  });
};

/** GROUP STANDINGS */
export interface ApiListGroupStandingsOptions {
  eventId?: ID;
  groupId?: ID;
}
export const getListGroupStandings = async (
  options: ApiListGroupStandingsOptions = {}
): Promise<GroupStanding[]> => {
  const params = new URLSearchParams();
  if (options.eventId) params.append("event_id", options.eventId.toString());
  if (options.groupId) params.append("group_id", options.groupId.toString());
  return apiFetch(`${API_ENDPOINTS.GROUP_STANDINGS}?${params.toString()}`);
};
